import { get, set } from 'idb-keyval'
import type { OfflineStation } from './buildKotSlip'

/**
 * Station routing cached for offline KOT building.
 *
 * Refreshed on every successful online poll. `fetchedAt` lets the UI warn when
 * the mapping is old enough that a recently-added category could route to the
 * wrong printer — stale routing is worse than obvious breakage, because the
 * ticket prints somewhere and nobody notices it went to the bar.
 */
const KEY = 'bhookly-offline-stations-v1'

export interface StationCache {
  fetchedAt: number
  stations: OfflineStation[]
  /** category_id → station_id, mirroring category_station_map. */
  categoryToStation: Record<string, string>
  /**
   * True when any active station is output_type='kds'. Those boards read
   * Postgres over SSE and cannot receive an offline ticket, so the cashier is
   * warned to relay those items by hand.
   */
  hasKdsStation: boolean
}

/** Beyond this the mapping is old enough to be worth flagging to the cashier. */
export const STATION_CACHE_STALE_MS = 24 * 60 * 60 * 1000

export async function readStationCache(): Promise<StationCache | null> {
  return (await get<StationCache>(KEY)) ?? null
}

export async function writeStationCache(
  c: Omit<StationCache, 'fetchedAt'>,
  now: number,
): Promise<void> {
  await set(KEY, { ...c, fetchedAt: now })
}

/** Never cached at all also counts as stale — there is nothing to trust. */
export function isStationCacheStale(c: StationCache | null, now: number): boolean {
  return c == null || now - c.fetchedAt > STATION_CACHE_STALE_MS
}
